/**
 * Farmhouse Kitchen & Bath — Predictive search SKU fix
 *
 * Problem: Empire's predictive search calls the Section Rendering API
 * (/search/suggest?section_id=predictive-search), which uses Shopify's
 * `predictive_search` Liquid object. That object has fixed search fields
 * (title, body, vendor, product_type, tags) and DOES NOT search variant SKUs.
 * Result: typing a SKU like "FFSGS6156-48" in the search bar returns 0 results.
 *
 * Fix: Intercept that fetch call and instead hit /search/suggest.json with
 * resources[options][fields] including variants.sku, then render the JSON
 * results into HTML matching predictive-search.liquid's existing markup.
 *
 * Safety:
 *   - Only intercepts the specific predictive-search section URL.
 *   - All other fetch calls pass through unmodified.
 *   - On any error, falls back to the original Shopify behavior.
 */
(function () {
  'use strict';

  if (window.__skuSearchFixActive) return;
  window.__skuSearchFixActive = true;

  var FIELDS = 'title,product_type,variants.sku,vendor,tag,body';
  var LIMIT = 10;

  var originalFetch = window.fetch.bind(window);

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatPrice(price) {
    if (price == null || price === '') return '';
    var num = typeof price === 'number' ? price : parseFloat(price);
    if (isNaN(num)) return '';
    try {
      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function' &&
        window.Theme &&
        window.Theme.moneyFormat
      ) {
        return window.Shopify.formatMoney(Math.round(num * 100), window.Theme.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return '$' + num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function resizeImage(url, size) {
    if (!url) return '';
    // Insert _SIZE before file extension (e.g. .jpg -> _70x.jpg)
    return url.replace(/(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_' + size + '$1$3');
  }

  function buildProductHTML(product) {
    var imageUrl = '';
    var imageAlt = product.title || '';
    if (product.featured_image && product.featured_image.url) {
      imageUrl = product.featured_image.url;
      if (product.featured_image.alt) imageAlt = product.featured_image.alt;
    } else if (product.image) {
      imageUrl = product.image;
    }

    var imageHtml = imageUrl
      ? '<img src="' + esc(resizeImage(imageUrl, '70x')) + '" alt="' + esc(imageAlt) + '" loading="lazy" width="70">'
      : '<div class="placeholder--image"></div>';

    var price = formatPrice(product.price);
    var compareNum = parseFloat(product.compare_at_price_max || product.compare_at_price || 0);
    var priceNum = parseFloat(product.price || 0);
    var onSale = compareNum > priceNum && compareNum > 0;

    var priceHtml;
    if (onSale) {
      priceHtml =
        '<span class="search-flydown--product-price search-flydown--product-price--on-sale">' +
          '<s class="search-flydown--product-compare-price">' + formatPrice(compareNum) + '</s> ' +
          '<span class="search-flydown--product-sale-price">' + price + '</span>' +
        '</span>';
    } else {
      priceHtml = '<span class="search-flydown--product-price">' + price + '</span>';
    }

    return (
      '<li>' +
        '<a class="search-flydown--product" href="' + esc(product.url) + '" ' +
           'aria-label="' + esc(product.title) + '">' +
          '<figure class="search-flydown--product-image">' + imageHtml + '</figure>' +
          '<div class="search-flydown--product-text">' +
            '<span class="search-flydown--product-title">' + esc(product.title) + '</span>' +
            priceHtml +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  function buildFlydownHTML(products, query) {
    if (!products || products.length === 0) {
      return (
        '<div>' +
          '<p class="search-flydown__no-results">No matching results found.</p>' +
        '</div>' +
        '<button class="search-flydown__continue" type="submit">' +
          'Search for &ldquo;' + esc(query) + '&rdquo;' +
        '</button>'
      );
    }

    var productsHtml = products.map(buildProductHTML).join('');

    return (
      '<div>' +
        '<div class="search-flydown__products-pages-posts">' +
          '<div class="search-flydown--product-items">' +
            '<h3 class="search-flydown__group-title">Products</h3>' +
            '<ul class="search-flydown__group-list">' + productsHtml + '</ul>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<button class="search-flydown__continue" type="submit">' +
        'View all results for &ldquo;' + esc(query) + '&rdquo;' +
      '</button>'
    );
  }

  function isPredictiveSearchURL(url) {
    if (!url || typeof url !== 'string') return false;
    return /\/search\/suggest(\?|$)/.test(url) && url.indexOf('section_id=predictive-search') !== -1;
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);

    if (!isPredictiveSearchURL(url)) {
      return originalFetch(input, init);
    }

    try {
      var urlObj = new URL(url, window.location.origin);
      var query = (urlObj.searchParams.get('q') || '').trim();

      if (!query) {
        return originalFetch(input, init);
      }

      // Strip any "filter:value AND " prefix Empire prepends via its filter dropdown
      var actualQuery = query;
      var andIdx = query.indexOf(' AND ');
      if (andIdx !== -1) {
        actualQuery = query.substring(andIdx + 5);
      }

      var jsonUrl =
        '/search/suggest.json' +
        '?q=' + encodeURIComponent(actualQuery) +
        '&resources[type]=product' +
        '&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=' + encodeURIComponent(FIELDS);

      return originalFetch(jsonUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('suggest.json status ' + response.status);
          return response.json();
        })
        .then(function (data) {
          var products =
            (data && data.resources && data.resources.results && data.resources.results.products) || [];
          var html = buildFlydownHTML(products, actualQuery);
          return new Response(html, {
            status: 200,
            headers: { 'Content-Type': 'text/html; charset=utf-8' },
          });
        })
        .catch(function (err) {
          if (window.console && console.warn) {
            console.warn('[sku-search-fix] Falling back to native predictive search:', err);
          }
          return originalFetch(input, init);
        });
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[sku-search-fix] Intercept error, falling back:', err);
      }
      return originalFetch(input, init);
    }
  };

  if (window.console && console.info) {
    console.info('[sku-search-fix] Active — predictive search now includes variant SKUs.');
  }
})();
